import React from 'react';
import Button from './Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExclamationTriangle,
  faClock,
  faTrashAlt,
  faFolder,
  faEye,
  faEyeSlash,
  faCopy,
  faDownload
} from '@fortawesome/free-solid-svg-icons';

/**
 * Composant réutilisable pour afficher un groupe de clés de cache
 * @param {Object} props
 * @param {string} props.groupName - Nom du groupe (ex: "config/read/languages")
 * @param {Array} props.entries - Liste des entrées du cache pour ce groupe
 * @param {Function} props.formatBytes - Fonction pour formater les octets
 * @param {Function} props.formatDuration - Fonction pour formater une durée
 * @param {Function} props.onClear - Fonction appelée pour vider le cache de ce groupe
 * @param {boolean} props.isClearing - Indique si le nettoyage est en cours
 * @param {Function} props.t - Fonction de traduction
 * @param {string} props.colorClass - Classe CSS pour la couleur du badge (optionnel)
 * @param {Object} props.icon - Icône FontAwesome pour le groupe (optionnel)
 */
const CacheGroupDisplay = ({ 
  groupName, 
  entries, 
  formatBytes, 
  formatDuration, 
  onClear, 
  isClearing, 
  t,
  colorStyle = { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' },
  icon = faFolder
}) => {
  const [expandedKeys, setExpandedKeys] = React.useState(new Set());
  
  // Calculer les statistiques du groupe
  const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0);
  const validEntries = entries.filter(entry => entry.isValid);
  const expiredEntries = entries.length - validEntries.length;

  const toggleKeyExpansion = (key) => {
    setExpandedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  return (
    <div className="wa-form-group wa-form-group-compact mb-6">
      <label className="wa-label">
        <FontAwesomeIcon icon={icon} className="mr-2" />
        <span className="px-2 py-1 rounded-full text-xs mr-2" style={colorStyle}>
          {groupName}
        </span>
        {t('ADMIN_CACHE', 'KEYS_COUNT', '{{count}} clés', { count: entries.length })}
      </label>
      <div className="flex items-center justify-between w-full">
        <div className="flex-1 space-y-1">
          <div className="flex items-center space-x-4 text-sm">
            <span className="flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-1" />
              {t('ADMIN_CACHE', 'VALID_ENTRIES', '{{count}} valides', { count: validEntries.length })}
            </span>
            {expiredEntries > 0 && (
              <span className="flex items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 mr-1" />
                {t('ADMIN_CACHE', 'EXPIRED_ENTRIES', '{{count}} expirées', { count: expiredEntries })}
              </span>
            )}
            <span className="text-gray-500">
              {formatBytes(totalSize)}
            </span>
          </div>
          <div className="w-full rounded-full h-2" style={{backgroundColor: 'var(--border-light)'}}>
            <div 
              className="h-2 rounded-full transition-all duration-300" 
              style={{ backgroundColor: 'var(--color-primary)', width: `${Math.round((validEntries.length / entries.length) * 100)}%` }}
            />
          </div>
        </div>
        {onClear && (
          <Button
            variant="warning"
            size="sm"
            loading={isClearing}
            className="ml-4 flex-shrink-0"
            onClick={() => onClear(groupName)}
            title={t('ADMIN_CACHE', 'CLEAR_GROUP', 'Vider le groupe {{group}}', { group: groupName })}
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </Button>
        )}
      </div>
      
      {/* Liste des clés individuelles */}
      {entries.length > 0 && (
        <div className="mt-3 pl-6 pr-2">
          <div className="text-xs font-medium mb-2" style={{color: 'var(--text-secondary)'}}>
            {t('ADMIN_CACHE', 'INDIVIDUAL_KEYS', 'Clés individuelles')}:
          </div>
          <div className="wa-table-container">
            <table className="wa-admin-table text-xs">
              <thead>
                <tr>
                  <th>{t('ADMIN_CACHE', 'CACHE_KEY', 'Clé de Cache')}</th>
                  <th>{t('ADMIN_CACHE', 'STATUS', 'Statut')}</th>
                  <th>{t('ADMIN_CACHE', 'SIZE', 'Taille')}</th>
                  <th>{t('ADMIN_CACHE', 'TTL', 'TTL')}</th>
                  <th className="text-center">{t('ADMIN_CACHE', 'ACTIONS', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => {
                  const ttlRemaining = entry.expiry - Date.now();
                  const isExpanded = expandedKeys.has(entry.key);
                  
                  // Calculer la hauteur adaptative pour le JSON
                  let jsonHeight = '24rem'; // Hauteur par défaut
                  if (isExpanded && entry.data) {
                    const jsonString = JSON.stringify(entry.data, null, 2);
                    const lineCount = jsonString.split('\n').length;
                    // 1 ligne ≈ 1.5rem, min 15rem, max 60rem
                    const calculatedHeight = Math.min(Math.max(lineCount * 1.5, 15), 60);
                    jsonHeight = `${calculatedHeight}rem`;
                  }
                  
                  return (
                    <React.Fragment key={idx}>
                      <tr className={!entry.isValid ? 'opacity-50' : ''}>
                        <td className="font-mono text-xs">
                          <div className="break-all max-w-3xl" title={entry.key}>
                            {entry.key}
                          </div>
                        </td>
                        <td>
                          <span 
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs"
                            style={{
                              backgroundColor: entry.isValid ? 'var(--color-success-light)' : 'var(--color-error-light)',
                              color: entry.isValid ? 'var(--color-success)' : 'var(--color-error)'
                            }}
                          >
                            <FontAwesomeIcon 
                              icon={entry.isValid ? faCheckCircle : faExclamationTriangle} 
                              className="mr-1" 
                            />
                            {entry.isValid 
                              ? t('ADMIN_CACHE', 'VALID', 'Valide')
                              : t('ADMIN_CACHE', 'EXPIRED', 'Expiré')
                            }
                          </span>
                        </td>
                        <td className="whitespace-nowrap" style={{color: 'var(--text-muted)'}}>
                          {formatBytes(entry.size)}
                        </td>
                        <td className="whitespace-nowrap" style={{color: 'var(--text-muted)'}}>
                          <span className="flex items-center">
                            <FontAwesomeIcon icon={faClock} className="mr-1" />
                            {entry.isValid ? formatDuration(ttlRemaining) : '-'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="wa-actions justify-center">
                            <button
                              onClick={() => toggleKeyExpansion(entry.key)}
                              className="transition-colors"
                              style={{color: 'var(--color-primary)'}}
                              title={isExpanded ? t('ADMIN_CACHE', 'HIDE_CONTENT', 'Masquer le contenu') : t('ADMIN_CACHE', 'VIEW_CONTENT', 'Voir le contenu')}
                            >
                              <FontAwesomeIcon icon={isExpanded ? faEyeSlash : faEye} />
                            </button>
                            <button
                              onClick={() => {
                                const dataToExport = {
                                  key: entry.key,
                                  value: entry.data,
                                  size: entry.size,
                                  isValid: entry.isValid,
                                  expiry: new Date(entry.expiry).toISOString(),
                                  exportDate: new Date().toISOString()
                                };
                                navigator.clipboard.writeText(JSON.stringify(dataToExport, null, 2))
                                  .then(() => {
                                    console.log('Copié:', entry.key);
                                  })
                                  .catch(err => console.error('Erreur copie:', err));
                              }}
                              className="transition-colors"
                              style={{color: 'var(--text-secondary)'}}
                              title={t('ADMIN_CACHE', 'COPY_ENTRY', 'Copier l\'entrée')}
                            >
                              <FontAwesomeIcon icon={faCopy} />
                            </button>
                            <button
                              onClick={() => {
                                const dataToExport = {
                                  key: entry.key,
                                  value: entry.data,
                                  size: entry.size,
                                  isValid: entry.isValid,
                                  expiry: new Date(entry.expiry).toISOString(),
                                  exportDate: new Date().toISOString()
                                };
                                const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                const sanitizedKey = entry.key.replace(/[^a-z0-9]/gi, '_').toLowerCase();
                                a.download = `cache_${sanitizedKey}_${new Date().toISOString().split('T')[0]}.json`;
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                URL.revokeObjectURL(url);
                              }}
                              className="transition-colors"
                              style={{color: 'var(--color-success)'}}
                              title={t('ADMIN_CACHE', 'EXPORT_ENTRY', 'Exporter l\'entrée')}
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{backgroundColor: 'var(--bg-secondary)'}}>
                          <td colSpan="5">
                            <div className="text-xs">
                              <div className="font-semibold mb-2" style={{color: 'var(--text-secondary)'}}>
                                {t('ADMIN_CACHE', 'CACHE_CONTENT', 'Contenu du cache')}:
                              </div>
                              <pre 
                                className="p-3 rounded overflow-auto text-xs"
                                style={{
                                  backgroundColor: 'var(--bg-primary)',
                                  border: '1px solid var(--border-light)',
                                  maxHeight: jsonHeight,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word'
                                }}
                              >
                                {JSON.stringify(entry.data, null, 2)}
                              </pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CacheGroupDisplay;
